/**
 * MarketplaceCartContext — vendor-scoped cart for the EntrepreNatives
 * marketplace /shop. Separate from `CartContext` (which drives the
 * vendor's own-website storefront keyed by subdomain).
 *
 * Rules (Shopify-style):
 *   • A cart holds items from ONE vendor at a time. If a shopper adds an
 *     item from a different vendor, we prompt them to clear the cart
 *     (the caller is expected to confirm first — this context exposes
 *     `wouldReplaceCart(item)` so the UI can show a confirm dialog).
 *   • Variants are honoured — the cart key is `${item_id}:${variant_id}`.
 *   • Persisted to localStorage under `marketplace_cart`.
 *   • Also tracks `buyer_email` + `shipping_address` so the checkout
 *     page can pick these up after refresh.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const KEY = 'marketplace_cart';
const EMAIL_KEY = 'marketplace_cart_email';
const ADDR_KEY = 'marketplace_cart_address';

const MarketplaceCartContext = createContext(null);

export function MarketplaceCartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(KEY);
      return saved ? JSON.parse(saved) : { vendor_id: null, vendor_name: '', items: [] };
    } catch {
      return { vendor_id: null, vendor_name: '', items: [] };
    }
  });
  const [buyerEmail, setBuyerEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || '');
  const [shippingAddress, setShippingAddress] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(ADDR_KEY) || 'null') || {
        name: '', line1: '', line2: '', city: '', state: '', zip: '', country: 'US',
      };
    } catch {
      return { name: '', line1: '', line2: '', city: '', state: '', zip: '', country: 'US' };
    }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    if (buyerEmail) localStorage.setItem(EMAIL_KEY, buyerEmail);
  }, [buyerEmail]);
  useEffect(() => {
    localStorage.setItem(ADDR_KEY, JSON.stringify(shippingAddress));
  }, [shippingAddress]);

  const lineCount = useMemo(
    () => cart.items.reduce((n, l) => n + (l.quantity || 0), 0),
    [cart.items],
  );

  const subtotal = useMemo(
    () => cart.items.reduce((s, l) => s + l.unit_price * l.quantity, 0),
    [cart.items],
  );

  const keyOf = (item_id, variant_id) => `${item_id}:${variant_id || ''}`;

  const wouldReplaceCart = useCallback(
    (vendor_id) => !!cart.vendor_id && cart.vendor_id !== vendor_id && cart.items.length > 0,
    [cart.vendor_id, cart.items.length],
  );

  const addToCart = useCallback((item, variant, qty = 1, options = {}) => {
    const vendor_id = item.vendor_id;
    const vendor_name = item.vendor?.business_name || '';
    setCart((prev) => {
      // Vendor-switch: clear if the shopper confirmed (options.replace === true).
      let next = prev;
      if (prev.vendor_id && prev.vendor_id !== vendor_id) {
        if (!options.replace) return prev;  // caller must prompt first
        next = { vendor_id, vendor_name, items: [] };
      } else if (!prev.vendor_id) {
        next = { vendor_id, vendor_name, items: [] };
      }
      const k = keyOf(item.item_id, variant?.variant_id);
      const existingIdx = next.items.findIndex(
        (l) => keyOf(l.item_id, l.variant_id) === k,
      );
      const unit_price = variant?.price != null ? variant.price : item.base_price;
      const variant_label = variant
        ? Object.values(variant.option_values || {}).filter(Boolean).join(' / ')
        : null;
      const image = variant?.image_url || (item.images || [])[0] || null;
      if (existingIdx >= 0) {
        const updated = [...next.items];
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: updated[existingIdx].quantity + qty,
        };
        return { ...next, items: updated };
      }
      return {
        ...next,
        items: [
          ...next.items,
          {
            item_id: item.item_id,
            variant_id: variant?.variant_id || null,
            title: item.title,
            variant_label,
            unit_price,
            quantity: qty,
            image,
          },
        ],
      };
    });
  }, []);

  const removeLine = useCallback((item_id, variant_id) => {
    const k = keyOf(item_id, variant_id);
    setCart((prev) => ({
      ...prev,
      items: prev.items.filter((l) => keyOf(l.item_id, l.variant_id) !== k),
    }));
  }, []);

  const updateQty = useCallback((item_id, variant_id, qty) => {
    const k = keyOf(item_id, variant_id);
    setCart((prev) => ({
      ...prev,
      items: prev.items
        .map((l) => (keyOf(l.item_id, l.variant_id) === k ? { ...l, quantity: Math.max(1, qty) } : l))
        .filter((l) => l.quantity > 0),
    }));
  }, []);

  const clearCart = useCallback(() => {
    setCart({ vendor_id: null, vendor_name: '', items: [] });
  }, []);

  const value = {
    cart,
    lineCount,
    subtotal,
    buyerEmail,
    setBuyerEmail,
    shippingAddress,
    setShippingAddress,
    addToCart,
    removeLine,
    updateQty,
    clearCart,
    wouldReplaceCart,
  };

  return (
    <MarketplaceCartContext.Provider value={value}>
      {children}
    </MarketplaceCartContext.Provider>
  );
}

export function useMarketplaceCart() {
  const ctx = useContext(MarketplaceCartContext);
  if (!ctx) throw new Error('useMarketplaceCart must be used inside <MarketplaceCartProvider>');
  return ctx;
}
